import { applicationContext, uuid, QueryUtility, toUTCDate, toDateOnlyString, parseDateOnly, getErrorMessage, resolveInitialEligibilityStatus } from 'core/utils';
import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import { action, computed, observable, reaction, runInAction, set, when } from 'mobx';
import { FacilityOrderService, FacilityOrderStatusService, ItemSubcategoryService, FacilityOrderTemplateValidationService } from "application/facility-order/services";
import { LookupService } from "common/services";
import { ItemCategoryService } from 'application/product/services';
import _ from 'lodash';
import { NikoPatientDetailService, NikoPatientTemplateService, NikoPatientTemplateItemService } from "application/niko-patient/services";
import { ModalParams } from 'core/models';
import { EligibilityStatusService } from 'application/eligibility-status/services';
import { FacilityInventoryCountDetailService } from "application/facility-inventory/services";
import { InventoryRequestService } from '../../inventory-request/services';
import { HistoryService } from '../../history/services';
import { ClinicalStatusService } from '../../clinical-status/services';
import { DiagnosisCodeService, LocalDoctorService } from '../../import/services';
import { MissingDocumentService } from '../../missing-document/services';
import { FacilityService } from '../../facility/services';
import { NikoPatientService } from '../../niko-patient/services';

@applicationContext
class NikoPatientSalesOrderViewStore extends BaseViewStore {
    @observable accessor facilityOrder;
    @observable accessor refreshKey = uuid();
    @observable accessor selectedQuestions = [];
    @observable accessor itemCategories = [];
    @observable accessor disabled = false;
    @observable accessor nikoPatientDetails = [];
    @observable accessor dosFilter = { start: null, end: null };
    @observable accessor tempDosFilter = { start: null, end: null };
    @observable accessor insuranceChangeHistoryMap = {};
    @observable accessor showInsuranceChangeHistory = null;
    @observable accessor tempFacility = null;
    @observable accessor tempPatient = null;
    @observable accessor tempItemCategory = null;
    @observable accessor tempBillingValidation = null;
    @observable accessor totalResults = 0;
    @observable accessor inactiveResults = 0;
    @observable accessor loadingFilter = false;

    @observable accessor showInactive = true;
    @observable accessor showInsurance = true;
    @observable accessor showClinical = false;
    @observable accessor clinicalLoadingCount = 0;

    @observable accessor isEmpty = false;
    @observable accessor facilityOrderTemplatesGroupByPatient = [];
    initialStickyParams = {};
    _suppressFilterRefresh = false;

    @computed
    get isFilterApplyAvailable() { return this.patientStore.value || this.itemCategoryStore.value || this.facilityStore.value || this.tempDosFilter.start || this.tempDosFilter.end || this.billingValidationStore.value; }

    questions = [{ id: "0", name: "Insurance questions" }, { id: "1", name: "Product questions" }];
    statuses = [{ id: "Hospital" }, { id: "Discharged" }, { id: "Expired" }, { id: "Active" }];
    billingValidations = [
        { id: "failed-import", name: "Failed Import Validations" },
        { id: "aob", name: "AOB Validations" },
        { id: "rental", name: "Rental Validations" },
        { id: "resolved", name: "Resolved Billing Notifications" }
    ];

    constructor(rootStore) {
        super(rootStore);

        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.isBiller = this.rootStore.userStore.user.roles.includes("Biller");
        this.pageName = 'patient-sales-order';

        this.inactiveStatuses = ['Hospital', 'Discharged', 'Expired'];

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.patientTemplateItemService = new NikoPatientTemplateItemService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);
        this.clinicalStatusService = new ClinicalStatusService(rootStore.application.baasic.apiClient);
        this.localDoctorService = new LocalDoctorService(rootStore.application.baasic.apiClient);
        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);
        this.missingDocumentService = new MissingDocumentService(rootStore.application.baasic.apiClient);
        this.facilityService = new FacilityService(rootStore.application.baasic.apiClient);
        this.nikoPatientService = new NikoPatientService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateValidationService = new FacilityOrderTemplateValidationService(rootStore.application.baasic.apiClient);

        this.questionsStore = new BaasicDropdownStore(
            { multi: true, filterable: false },
            {
                fetchFunc: () => {
                    return this.questions;
                },
            }
        );

        this.billingValidationStore = new BaasicDropdownStore(
            { filterable: false },
            {
                fetchFunc: async () => {
                    const ctx = this.getCrossFilterContext('billingValidation');
                    const params = {
                        ...ctx,
                        itemCategoryIds: ctx.itemCategoryId ? [ctx.itemCategoryId] : undefined,
                        itemCategoryId: undefined,
                        isSalesOrder: true
                    };
                    const response = await this.patientTemplateService.getAvailableBillingValidations(params);
                    const available = response.data.availableBillingValidations;
                    if (!available) return this.billingValidations;
                    return this.billingValidations.filter(v => available.includes(v.id));
                },
            }
        );

        this.statusConfirmModal = new ModalParams({ onClose: null });
        this.statusConfirmModal.onCancel = () => { this.statusConfirmModal.close() };
        this.statusConfirmModal.yesLabel = 'MODAL.START_ANYWAYS_LABEL';
        this.statusConfirmModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.statusConfirmModal.swapButtons = true;

        this.facilityStore = new BaasicDropdownStore(
            { filterable: true },
            {
                fetchFunc: async (term) => {
                    const response = await this.facilityService.find({ searchQuery: term, rpp: 10, isNiko: true, ...this.getCrossFilterContext('facility') });
                    return response.data.item;
                }
            }
        );
        this.patientStore = new BaasicDropdownStore(
            { filterable: true },
            {
                fetchFunc: async (term) => {
                    const response = await this.nikoPatientService.find({ searchQuery: term, rpp: 10, ...this.getCrossFilterContext('patient') });
                    return response.data.item.map(person => ({ id: person.id, name: `${person.lastName}, ${person.firstName} (${person.displayId})` }));
                }
            }
        );
        this.itemCategoryStore = new BaasicDropdownStore(
            { filterable: false },
            {
                fetchFunc: async (term) => {
                    const response = await this.itemCategoryService.find({ searchQuery: term, rpp: 100, ...this.getCrossFilterContext('category') });
                    return response.data.item.filter(i => i.abrv == "enteral" || i.abrv == "respiratory" || i.abrv == "urological" || i.abrv == "ostomy");
                },
            });

        this.nikoPatientInsuranceQuestionModal = new ModalParams({});
        this.nikoPatientInsuranceInfoNoteModal = new ModalParams({});
        this.nikoPatientEligibilityStatusNoteModal = new ModalParams({});
        this.nikoTemplateCreateModal = new ModalParams({});
        this.nikoTemplateMissingProductNoteModal = new ModalParams({});
        this.nikoTemplateInvoiceNoteModal = new ModalParams({});
        this.patientTemplateDoNotSupplyModal = new ModalParams({});
        this.patientTemplateDiscontinuedModal = new ModalParams({});
        this.nikoPatientStatusModal = new ModalParams({});
        this.patientTemplateDeleteModal = new ModalParams({});
        this.nikoPatientTemplateItemCalculationModal = new ModalParams({});
        this.nikoFacilityOrderReasonForChangeModal = new ModalParams({});
        this.changeClinicalStatusModal = new ModalParams({});
        this.nikoPatientTemplateItemNewFlowDeleteQuestionModal = new ModalParams({});
        this.patientTemplateNoteModal = new ModalParams({});
        this.nikoTemplateMissingDocumentNoteModal = new ModalParams({});
        this.patientTemplateClinicalStatusNoteModal = new ModalParams({});
        this.templatePNMissingInformationModal = new ModalParams({});
        this.filterModal = new ModalParams({});

        this.templateChangeConfirmModal = new ModalParams({ onClose: null });
        this.templateChangeConfirmModal.onConfirm = async () => { this.templateChangeConfirmModal.close(); };
        this.templateChangeConfirmModal.yesLabel = 'MODAL.ACKNOWLEDGE';
        this.templateChangeConfirmModal.doNotShowCancel = true;

        this.nikoFacilityOrderUSIntakeFindAndReplaceModal = new ModalParams({});
        this.inventoryRequestService = new InventoryRequestService(rootStore.application.baasic.apiClient);

        this.pageViewStore = {
            patientTemplateService: this.patientTemplateService,
            patientTemplateItemService: this.patientTemplateItemService,
            nikoPatientDetailService: this.nikoPatientDetailService,
            clinicalStatusService: this.clinicalStatusService,
            localDoctorService: this.localDoctorService,
            diagnosisCodeService: this.diagnosisCodeService,
            nikoTemplateMissingDocumentNoteModal: this.nikoTemplateMissingDocumentNoteModal,
            patientTemplateClinicalStatusNoteModal: this.patientTemplateClinicalStatusNoteModal,
            templatePNMissingInformationModal: this.templatePNMissingInformationModal,
            clinicalLoadStart: this.clinicalLoadStart,
            clinicalLoadEnd: this.clinicalLoadEnd,
            openChangeClinicalStatusModal: this.openChangeClinicalStatusModal,
        };

        this.queryUtility = new QueryUtility(this.rootStore, async () => { await this.applyFilters() }, {});

        reaction(() => this.facilityStore.value, () => this.refreshDependentFilterOptions('facility'));
        reaction(() => this.patientStore.value, () => this.refreshDependentFilterOptions('patient'));
        reaction(() => this.itemCategoryStore.value, () => this.refreshDependentFilterOptions('category'));
        reaction(() => this.billingValidationStore.value, () => this.refreshDependentFilterOptions('billingValidation'));

        this.fetch([
            this.initialize()
        ]);
    }

    getCrossFilterContext(excludeKey) {
        const ctx = {};
        if (excludeKey !== 'facility' && this.facilityStore && this.facilityStore.value && this.facilityStore.value.id) {
            ctx.facilityId = this.facilityStore.value.id;
        }
        if (excludeKey !== 'patient' && this.patientStore && this.patientStore.value && this.patientStore.value.id) {
            ctx.nikoPatientId = this.patientStore.value.id;
        }
        if (excludeKey !== 'category' && this.itemCategoryStore && this.itemCategoryStore.value && this.itemCategoryStore.value.id) {
            ctx.itemCategoryId = this.itemCategoryStore.value.id;
        }
        if (excludeKey !== 'billingValidation' && this.billingValidationStore && this.billingValidationStore.value && this.billingValidationStore.value.id) {
            ctx.billingValidation = this.billingValidationStore.value.id;
        }
        if (excludeKey !== 'dos') {
            const dosStart = this.tempDosFilter.start;
            const dosEnd = this.tempDosFilter.end;
            if ((dosStart || dosEnd) && !(dosStart && dosEnd && dosStart > dosEnd)) {
                if (dosStart) ctx.dosDateFrom = toDateOnlyString(dosStart);
                if (dosEnd) ctx.dosDateTo = toDateOnlyString(dosEnd);
            }
        }
        return ctx;
    }

    @action.bound
    refreshDependentFilterOptions(changedKey) {
        if (this._suppressFilterRefresh) return;
        const refreshers = {
            facility: this.facilityStore,
            patient: this.patientStore,
            category: this.itemCategoryStore,
            billingValidation: this.billingValidationStore,
        };
        Object.keys(refreshers).forEach(key => {
            if (key !== changedKey) {
                const store = refreshers[key];
                if (store && store.actions && store.actions.onFilter) {
                    store.actions.onFilter(store.filterTerm);
                }
            }
        });
    }

    async initialize() {
        this.page = "patient-sales-order";
        await this.getLookups();

        const hasFilters = await this.restoreFiltersFromUrl();
        if (hasFilters) {
            const facilityId = this.facilityStore.value && this.facilityStore.value.id;
            const patientId = this.patientStore.value && this.patientStore.value.id;
            const itemCategoryId = this.itemCategoryStore.value && this.itemCategoryStore.value.id;
            const billingValidation = this.billingValidationStore.value && this.billingValidationStore.value.id;

            if (facilityId || patientId || itemCategoryId || billingValidation || this.dosFilter.start || this.dosFilter.end || this.queryUtility.filter.patientTemplateIds) {
                await this.fetchSalesOrderData({ facilityId, patientId, itemCategoryId, billingValidation });
            }
        } else {
            this.filterModal.open();
        }
    }

    @action.bound
    pushFiltersToUrl() {
        const params = { ...this.initialStickyParams };
        if (this.tempFacility && this.tempFacility.id) params.facilityId = this.tempFacility.id;
        if (this.tempPatient && this.tempPatient.id) params.patientId = this.tempPatient.id;
        if (this.tempItemCategory && this.tempItemCategory.id) params.itemCategoryId = this.tempItemCategory.id;
        if (this.dosFilter.start) params.dosDateFrom = toDateOnlyString(this.dosFilter.start);
        if (this.dosFilter.end) params.dosDateTo = toDateOnlyString(this.dosFilter.end);
        if (this.tempBillingValidation && this.tempBillingValidation.id) params.billingValidation = this.tempBillingValidation.id;

        this.rootStore.routerStore.setQueryParams(params);
    }

    @action.bound
    async restoreFiltersFromUrl() {
        const queryParams = this.rootStore.routerStore.routerState.queryParams;
        if (!queryParams) return false;

        const { facilityId, patientId, itemCategoryId, dos, hasMissingDocs, clinicalStatusId, isUserDns, isSystemDns, dosDateFrom, dosDateTo, billingValidation, expandClinical, patientTemplateIds } = queryParams;
        const hasModalFilters = facilityId || patientId || itemCategoryId || dos || dosDateFrom || dosDateTo || billingValidation || patientTemplateIds;

        if (!hasModalFilters) return false;

        this._suppressFilterRefresh = true;
        try {
        if (facilityId) {
            const facility = (await this.facilityService.get(facilityId)).data;
            this.facilityStore.setValue(facility);
            this.queryUtility.filter.set('facilityId', facility);
            this.tempFacility = facility;
        }
        if (patientId) {
            const person = (await this.nikoPatientService.get(patientId)).data;
            const patient = { id: person.id, name: `${person.lastName}, ${person.firstName} (${person.displayId})` };
            this.patientStore.setValue(patient);
            this.queryUtility.filter.set('patientId', patient);
            this.tempPatient = patient;
        }
        if (itemCategoryId) {
            const category = this.lookups.itemCategory.find(x => x.id == itemCategoryId);
            this.itemCategoryStore.setValue(category);
            this.queryUtility.filter.set('itemCategoryId', category);
            this.tempItemCategory = category;
        }
        if (dos) {
            const dateValue = new Date(dos);
            if (!isNaN(dateValue.getTime())) {
                this.dosFilter = { start: dateValue, end: dateValue };
                this.tempDosFilter = { start: dateValue, end: dateValue };
            }
        }
        if (dosDateFrom || dosDateTo) {
            const start = dosDateFrom ? parseDateOnly(dosDateFrom) : null;
            const end = dosDateTo ? parseDateOnly(dosDateTo) : null;
            this.dosFilter = { start: start, end: end };
            this.tempDosFilter = { start: start, end: end };
        }
        if (hasMissingDocs) {
            this.queryUtility.filter.set('hasMissingDocs', hasMissingDocs === 'true');
            this.initialStickyParams.hasMissingDocs = hasMissingDocs;
        }
        if (clinicalStatusId) {
            this.queryUtility.filter.set('clinicalStatusId', clinicalStatusId);
            this.initialStickyParams.clinicalStatusId = clinicalStatusId;
        }
        if (isUserDns) {
            this.queryUtility.filter.set('isUserDns', isUserDns === 'true');
            this.initialStickyParams.isUserDns = isUserDns;
        }
        if (isSystemDns) {
            this.queryUtility.filter.set('isSystemDns', isSystemDns === 'true');
            this.initialStickyParams.isSystemDns = isSystemDns;
        }
        if (dosDateFrom) {
            this.queryUtility.filter.set('dosDateFrom', dosDateFrom);
            this.initialStickyParams.dosDateFrom = dosDateFrom;
        }
        if (dosDateTo) {
            this.queryUtility.filter.set('dosDateTo', dosDateTo);
            this.initialStickyParams.dosDateTo = dosDateTo;
        }
        if (billingValidation) {
            const validationOption = this.billingValidations.find(x => x.id === billingValidation);
            if (validationOption) {
                this.billingValidationStore.setValue(validationOption);
                this.queryUtility.filter.set('billingValidation', validationOption);
                this.tempBillingValidation = validationOption;
            }
        }
        // ISG-2727 (part of ISG-2474): the View links on the multi-DOS credit pop-up open this page filtered to an
        // explicit set of PSOs. Kept sticky so re-applying the modal filters does not silently drop it.
        if (patientTemplateIds) {
            this.queryUtility.filter.set('patientTemplateIds', patientTemplateIds);
            this.initialStickyParams.patientTemplateIds = patientTemplateIds;
        }
        if (expandClinical === 'true') {
            this.showClinical = true;
        }
        } finally {
            this._suppressFilterRefresh = false;
        }
        this.refreshDependentFilterOptions(null);
        return true;
    }

    @action.bound
    async getFacilityOrder() {
        this.facilityOrder = (await this.facilityOrderService.find({ isNiko: true, embed: 'Facility,FacilityOrderStatuses' })).data;
    }

    @action.bound
    async getLookups() {
        this.lookups = await this.lookupService.getLookups("FacilityScheduleType,NewFlowStatus,ImportHeaderStatus,ImportAction,ItemCategory,SubCategory,SpecialtyType,DefaultItemType,SpecialtyType");
        this.orderingTypeId = this.lookups.facilityScheduleType.find((s) => s.abrv === 'ordering').id;
        this.inProgressStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'in-progress').id;
        this.changeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'change').id;
        this.noChangeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'no-change').id;
        this.dischargeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'discharge').id;
        this.activeNewFlowStatusIds = `${this.inProgressStatusId},${this.changeStatusId},${this.noChangeStatusId}`;
        this.specialtyTypeLookup = this.lookups.specialtyType;

        this.insertImportActionId = this.lookups.importAction.find((s) => s.abrv === 'insert').id;
        this.deleteImportActionId = this.lookups.importAction.find((s) => s.abrv === 'delete').id;
        this.updateImportActionId = this.lookups.importAction.find((s) => s.abrv === 'update').id;

        this.ostomyItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'ostomy').id;
        this.enteralItemCategoryId = this.lookups.itemCategory.find((s) => s.abrv === 'enteral').id;
        this.respiratorySubCategoryId = this.lookups.subCategory.find((s) => s.abrv === 'Respiratory---trachs-supply').id;

        this.syringeDefaultItemTypeId = this.lookups.defaultItemType.find((s) => s.abrv === 'syringe').id;

        const eligibilityStatusService = new EligibilityStatusService(this.rootStore.application.baasic.apiClient);
        this.eligibilityStatuses = (await eligibilityStatusService.find({ rpp: 100 })).data.item;
    }

    @action.bound
    async applyFilters() {
        this.loadingFilter = true;
        this.tempFacility = this.facilityStore.value;
        this.tempPatient = this.patientStore.value;
        this.tempItemCategory = this.itemCategoryStore.value;
        this.tempBillingValidation = this.billingValidationStore.value;

        const facilityId = this.facilityStore.value && this.facilityStore.value.id;
        const patientId = this.patientStore.value && this.patientStore.value.id;
        const itemCategoryId = this.itemCategoryStore.value && this.itemCategoryStore.value.id;
        const billingValidation = this.billingValidationStore.value && this.billingValidationStore.value.id;
        this.dosFilter = { start: this.tempDosFilter.start, end: this.tempDosFilter.end };

        if (facilityId || patientId || itemCategoryId || this.dosFilter.start || this.dosFilter.end || billingValidation)
            await this.fetchSalesOrderData({ facilityId: facilityId, patientId: patientId, itemCategoryId: itemCategoryId, billingValidation: billingValidation });
        else
            this.nikoPatientDetails = [];

        if ((this.dosFilter.start || this.dosFilter.end) && this.nikoPatientDetails.length === 0) {
            this.rootStore.notificationStore.error("There are no PSOs to display for the selected date range.");
            this.dosFilter = { start: null, end: null };
            this.tempDosFilter = { start: null, end: null };
            this.loadingFilter = false;
            return;
        }

        this.refreshKey = uuid();
        this.filterModal.close();
        this.pushFiltersToUrl();
        this.loadingFilter = false;
    }

    @action.bound
    clearFilters() {
        this._suppressFilterRefresh = true;
        try {
            this.dosFilter = { start: null, end: null };
            this.tempDosFilter = { start: null, end: null };

            this.facilityStore.setValue(null);
            this.patientStore.setValue(null);
            this.itemCategoryStore.setValue(null);
            this.billingValidationStore.setValue(null);

            this.tempFacility = null;
            this.tempPatient = null;
            this.tempItemCategory = null;
            this.tempBillingValidation = null;

            this.queryUtility.filter.set('facilityId', null);
            this.queryUtility.filter.set('patientId', null);
            this.queryUtility.filter.set('itemCategoryId', null);
            this.queryUtility.filter.set('billingValidation', null);

            this.nikoPatientDetails = [];
            this.rootStore.routerStore.setQueryParams({ ...this.initialStickyParams });
        } finally {
            this._suppressFilterRefresh = false;
        }
        this.refreshDependentFilterOptions(null);
    }

    @action.bound
    async fetchSalesOrderData(filter) {
        const hasMissingDocs = this.queryUtility.filter.hasMissingDocs;
        const clinicalStatusId = this.queryUtility.filter.clinicalStatusId;
        const isSystemDns = this.queryUtility.filter.isSystemDns;
        const isUserDns = this.queryUtility.filter.isUserDns;
        // ISG-2727: comma separated PSO (patient template) ids coming from the multi-DOS credit pop-up View links.
        const patientTemplateIds = this.queryUtility.filter.patientTemplateIds;

        let params = {
            ids: patientTemplateIds ? patientTemplateIds.split(',') : undefined,
            facilityId: filter.facilityId ? filter.facilityId : undefined,
            nikoPatientId: filter.patientId ? filter.patientId : undefined,
            itemCategoryIds: filter.itemCategoryId ? [filter.itemCategoryId] : undefined,
            billingValidation: filter.billingValidation ? filter.billingValidation : undefined,
            hasMissingDocs: hasMissingDocs,
            clinicalStatusId: clinicalStatusId ? clinicalStatusId : undefined,
            isSystemDns: isSystemDns,
            isUserDns: isUserDns,
            dosDateFrom: this.dosFilter.start ? toDateOnlyString(this.dosFilter.start) : undefined,
            dosDateTo: this.dosFilter.end ? toDateOnlyString(this.dosFilter.end) : undefined,
            isSalesOrder: true,
            pageSize: 10000,
            embed: 'NikoPatientDetail,FacilityOrder,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.Facility,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory,FacilityOrderTemplateValidations',
            orderBy: "SubCategory",
            orderDirection: "asc",
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;

        await this.attachNikoInvoiceIds(responseData);

        const missingDocumentLookup = (await this.missingDocumentService.find({ rpp: 100, isActive: true, sort: "name|asc" })).data.item;

        // Resolve the previous (initial) clinical status name so the header history tooltip can show it.
        const initialClinicalStatusIds = [...new Set(responseData
            .map(e => e.initialPatientTemplate && e.initialPatientTemplate.ClinicalStatusId)
            .filter(Boolean))];
        const clinicalStatusNameById = {};
        await Promise.all(initialClinicalStatusIds.map(async (csId) => {
            const clinicalStatus = (await this.clinicalStatusService.get(csId)).data;
            clinicalStatusNameById[csId] = clinicalStatus && clinicalStatus.name;
        }));
        responseData.forEach(element => {
            element.missingDocumentStore = new BaasicDropdownStore(
                { multi: true, filterable: true, dataItemKey: "id" },
                {
                    fetchFunc: async (term = "") => {
                        const filteredData = missingDocumentLookup.filter(mc => (mc.itemCategoryId === element.subCategory.itemCategoryId || !mc.itemCategoryId) && mc.name.toLowerCase().includes(term.toLowerCase()));
                        let filteredMissingDocuments = await this.filterMissingDocumentStore(filteredData, element);
                        let mappedResult = _.map(filteredMissingDocuments, (item) => {
                            return _.extend(item, { missingDocuments: item.id });
                        });
                        return mappedResult;
                    }
                }
            )
        });

        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: false,
            facilityOrder: { ...patientTemplates[0].facilityOrder },
            tempHeight: patientTemplates[0].nikoPatientDetail.height,
            tempWeight: patientTemplates[0].nikoPatientDetail.weight,
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
                itemsVisible: false
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            nikoPatientDetail.history = { userName: '', eventDate: null, fieldName: '', action: '' };
            nikoPatientDetail.isActive = !this.inactiveStatuses.includes(nikoPatientDetail.status);
            nikoPatientDetail.showInsurance = this.showInsurance;
            nikoPatientDetail.setShowInsurance = this.setShowInsurance;
            this.setHasHistory(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.setShowClinical = this.setShowClinical;
                template.showClinical = this.showClinical;
                template.isEditDisabled = true;
                template.history = template.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                if (template.initialPatientTemplate && template.initialPatientTemplate.ClinicalStatusId) {
                    template.initialPatientTemplate.ClinicalStatusName = clinicalStatusNameById[template.initialPatientTemplate.ClinicalStatusId];
                }
                template.isDifferentQty = template.patientTemplateItems && template.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
                template.patientTemplateItems.forEach(item => {
                    item.history = item.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                });
            });
        });

        this.totalResults = nikoPatientDetails.reduce((sum, np) => sum + np.patientTemplates.length, 0);
        const inactivePatientTemplates = nikoPatientDetails
            .filter(x => this.inactiveStatuses.includes(x.status))
            .reduce((sum, np) => sum + np.patientTemplates.length, 0);

        const discontinuedFromActivePatients = nikoPatientDetails
            .filter(x => !this.inactiveStatuses.includes(x.status))
            .reduce((sum, np) => sum + np.patientTemplates.filter(t => t.isDiscontinued).length, 0);

        this.inactiveResults = inactivePatientTemplates + discontinuedFromActivePatients;

        nikoPatientDetails.sort((a, b) => {
            const dosA = a.facilityOrder && a.facilityOrder.dateOfService ? new Date(a.facilityOrder.dateOfService) : new Date(0);
            const dosB = b.facilityOrder && b.facilityOrder.dateOfService ? new Date(b.facilityOrder.dateOfService) : new Date(0);
            if (dosB - dosA !== 0) return dosB - dosA;

            const facilityA = (a.facility && a.facility.name || '').toLowerCase();
            const facilityB = (b.facility && b.facility.name || '').toLowerCase();
            if (facilityA < facilityB) return -1;
            if (facilityA > facilityB) return 1;

            const lastNameA = (a.nikoPatient && a.nikoPatient.lastName || '').toLowerCase();
            const lastNameB = (b.nikoPatient && b.nikoPatient.lastName || '').toLowerCase();
            if (lastNameA < lastNameB) return -1;
            if (lastNameA > lastNameB) return 1;

            return 0;
        });

        this.nikoPatientDetails = nikoPatientDetails;
    }

    @action.bound
    triggerShowInactive() {
        this.showInactive = !this.showInactive;
    }

    @action.bound
    triggerShowInsurance() {
        this.showInsurance = !this.showInsurance;

        this.nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.showInsurance = this.showInsurance;
        });
    }

    @action.bound
    triggerShowClinical() {
        if (this.clinicalLoadingCount > 0) return;

        this.showClinical = !this.showClinical;

        if (!this.showClinical) {
            this.clinicalLoadingCount = 0;
        }

        this.nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.showClinical = this.showClinical;
            });
        });
    }

    @action.bound
    clinicalLoadStart() {
        this.clinicalLoadingCount++;
    }

    @action.bound
    clinicalLoadEnd() {
        this.clinicalLoadingCount = Math.max(0, this.clinicalLoadingCount - 1);
    }

    @action.bound
    setShowClinical(nikoPatientDetail) {
        nikoPatientDetail.showClinical = !nikoPatientDetail.showClinical;
    }

    @action.bound
    setShowInsurance(nikoPatientDetail) {
        nikoPatientDetail.showInsurance = !nikoPatientDetail.showInsurance;
    }

    // ISG-2532: Edit mode removed from the PSO page - clinical fields are now always editable.
    // Toggle kept (commented) in case edit mode needs to be restored.
    // @action.bound
    // setEditMode(template) {
    //     template.isEditDisabled = !template.isEditDisabled;
    // }

    @action.bound
    async fetchPatientTemplateByPatient(nikoPatientDetailId) {
        let index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        const facilityId = this.tempFacility && this.tempFacility.id;
        const itemCategoryId = this.tempItemCategory && this.tempItemCategory.id;
        const billingValidation = this.tempBillingValidation && this.tempBillingValidation.id;
        const hasMissingDocs = this.queryUtility.filter.hasMissingDocs;
        const clinicalStatusId = this.queryUtility.filter.clinicalStatusId;
        const isSystemDns = this.queryUtility.filter.isSystemDns;
        const isUserDns = this.queryUtility.filter.isUserDns;

        let params = {
            facilityId: facilityId ? facilityId : undefined,
            itemCategoryIds: itemCategoryId ? [itemCategoryId] : undefined,
            billingValidation: billingValidation ? billingValidation : undefined,
            hasMissingDocs: hasMissingDocs,
            clinicalStatusId: clinicalStatusId ? clinicalStatusId : undefined,
            isSystemDns: isSystemDns,
            isUserDns: isUserDns,
            dosDateFrom: this.dosFilter.start ? toDateOnlyString(this.dosFilter.start) : undefined,
            dosDateTo: this.dosFilter.end ? toDateOnlyString(this.dosFilter.end) : undefined,
            isSalesOrder: true,
            pageSize: 10000,
            embed: 'NikoPatientDetail,FacilityOrder,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.Facility,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory,FacilityOrderTemplateValidations',
            orderBy: "SubCategory",
            orderDirection: "asc",
            nikoPatientDetailId: nikoPatientDetailId
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;

        await this.attachNikoInvoiceIds(responseData);

        const missingDocumentLookup = (await this.missingDocumentService.find({ rpp: 100, isActive: true, sort: "name|asc" })).data.item;

        // Resolve the previous (initial) clinical status name so the header history tooltip can show it.
        const initialClinicalStatusIds = [...new Set(responseData
            .map(e => e.initialPatientTemplate && e.initialPatientTemplate.ClinicalStatusId)
            .filter(Boolean))];
        const clinicalStatusNameById = {};
        await Promise.all(initialClinicalStatusIds.map(async (csId) => {
            const clinicalStatus = (await this.clinicalStatusService.get(csId)).data;
            clinicalStatusNameById[csId] = clinicalStatus && clinicalStatus.name;
        }));
        responseData.forEach(element => {
            element.missingDocumentStore = new BaasicDropdownStore(
                { multi: true, filterable: true, dataItemKey: "id" },
                {
                    fetchFunc: async (term = "") => {
                        const filteredData = missingDocumentLookup.filter(mc => (mc.itemCategoryId === element.subCategory.itemCategoryId || !mc.itemCategoryId) && mc.name.toLowerCase().includes(term.toLowerCase()));
                        let filteredMissingDocuments = await this.filterMissingDocumentStore(filteredData, element);
                        let mappedResult = _.map(filteredMissingDocuments, (item) => {
                            return _.extend(item, { missingDocuments: item.id });
                        });
                        return mappedResult;
                    }
                }
            )
        });

        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: false,
            facilityOrder: { ...patientTemplates[0].facilityOrder },
            tempHeight: patientTemplates[0].nikoPatientDetail.height,
            tempWeight: patientTemplates[0].nikoPatientDetail.weight,
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
                itemsVisible: false
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            nikoPatientDetail.history = { userName: '', eventDate: null, fieldName: '', action: '' };
            nikoPatientDetail.isActive = !this.inactiveStatuses.includes(nikoPatientDetail.status);
            nikoPatientDetail.showInsurance = this.showInsurance;
            nikoPatientDetail.setShowInsurance = this.setShowInsurance;
            this.setHasHistory(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.setShowClinical = this.setShowClinical;
                template.showClinical = this.showClinical;
                template.isEditDisabled = true;
                template.history = template.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                if (template.initialPatientTemplate && template.initialPatientTemplate.ClinicalStatusId) {
                    template.initialPatientTemplate.ClinicalStatusName = clinicalStatusNameById[template.initialPatientTemplate.ClinicalStatusId];
                }
                template.isDifferentQty = template.patientTemplateItems && template.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
                template.patientTemplateItems.forEach(item => {
                    item.history = item.history || { userName: '', eventDate: null, fieldName: '', action: '' };
                });
            });
        });

        index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        if (nikoPatientDetails && nikoPatientDetails[0]) {
            const existingDetail = this.nikoPatientDetails[index];
            const existingTemplates = (existingDetail && existingDetail.patientTemplates) || [];

            const templateOrderById = {};
            existingTemplates.forEach((t, i) => { templateOrderById[t.id] = i; });
            nikoPatientDetails[0].patientTemplates = _.sortBy(nikoPatientDetails[0].patientTemplates, t =>
                templateOrderById[t.id] != null ? templateOrderById[t.id] : Number.MAX_SAFE_INTEGER
            );

            nikoPatientDetails[0].patientTemplates.forEach(newTemplate => {
                const existingTemplate = existingTemplates.find(t => t.id == newTemplate.id);
                const existingItems = (existingTemplate && existingTemplate.patientTemplateItems) || [];
                if (existingItems.length === 0) return;
                const itemOrderById = {};
                existingItems.forEach((it, i) => { itemOrderById[it.id] = i; });
                newTemplate.patientTemplateItems = _.sortBy(newTemplate.patientTemplateItems, item =>
                    itemOrderById[item.id] != null ? itemOrderById[item.id] : Number.MAX_SAFE_INTEGER
                );
            });

            runInAction(() => {
                Object.assign(this.nikoPatientDetails[index], nikoPatientDetails[0]);
            });
        }
        else {
            this.nikoPatientDetails.splice(index, 1);
        }
    }

    // ISG-3039: refresh the cards of the given PSOs after a clinical status change / credit. A multi-DOS credit
    // touches PSOs of other DOS, which sit in other patient detail groups, so every group holding one of them is
    // re-fetched - not just the source PSO's. PSOs that are not on the page (filtered out) are skipped.
    @action.bound
    async refreshPatientTemplatesByIds(patientTemplateIds) {
        const ids = (patientTemplateIds || []).filter(Boolean).map(id => id.toLowerCase());
        if (!ids.length) return;

        const nikoPatientDetailIds = this.nikoPatientDetails
            .filter(d => (d.patientTemplates || []).some(t => ids.includes((t.id || '').toLowerCase())))
            .map(d => d.id);

        await Promise.all(nikoPatientDetailIds.map(id => this.fetchPatientTemplateByPatient(id)));

        // The Clinical section keeps the template it was mounted with (its store copies it into the form once), so
        // without a remount it keeps showing the old Clinical Status. A new key remounts it for the refreshed PSOs
        // only.
        runInAction(() => {
            this.nikoPatientDetails.forEach(d => (d.patientTemplates || []).forEach(t => {
                if (ids.includes((t.id || '').toLowerCase())) {
                    set(t, 'clinicalEditKey', uuid());
                }
            }));
        });
    }

    async filterMissingDocumentStore(missingDocuments, patientTemplate) {
        let result = missingDocuments;
        if (missingDocuments && missingDocuments.some(m => m)) {
            const patientTemplateItems = patientTemplate.patientTemplateItems;

            const diabeticFormulaSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "diabetic-formula").id;
            const renalFormulaNeproNRSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "renal-formula-nepro-nr").id;
            const renalFormulaSuplenaSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "renal-formula-suplena").id;
            const pulmonaryFormulaSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "pulmonary-formula").id;
            const elementalFormulaSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "elemental-formula").id;
            const proteinSupplementSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "protein-supplement").id;
            const siliconeCatheterSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "silicone-catheter").id;
            const coudeCatheterSpecialtyTypeId = this.specialtyTypeLookup.find(x => x.abrv === "coude-catheter").id;

            if (patientTemplateItems.length > 0)
                await when(() => patientTemplateItems.every(item => item.product !== undefined && item.product !== null));

            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == diabeticFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-diabetic");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == renalFormulaNeproNRSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-nepro");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == renalFormulaSuplenaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-renal-suplena");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == pulmonaryFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-pulmonary");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == elementalFormulaSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-elemental");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId === proteinSupplementSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-protein-supplement");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == siliconeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-silicone-catheter");
            }
            if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == coudeCatheterSpecialtyTypeId))) {
                result = result.filter(i => i.abrv !== "specialty-product-justification-coude-catheter");
            }
        }
        return result;
    }

    @action.bound
    async fetchPatientTemplateItemsById(nikoPatientDetailId, patientTemplateId) {
        const index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;
        var patientTemplate = this.nikoPatientDetails[index].patientTemplates && this.nikoPatientDetails[index].patientTemplates.find(x => x.id == patientTemplateId);
        if (patientTemplate && patientTemplate.patientTemplateItems) {
            let responseData = (await this.patientTemplateService.get(patientTemplateId, { embed: 'PatientTemplateItems' })).data;
            if (responseData.patientTemplateItems) {
                runInAction(() => {
                    Object.assign(patientTemplate.patientTemplateItems, responseData.patientTemplateItems);
                });
            }
        }
    }

    @action.bound
    setHasHistory(nikoPatientDetail) {
        this.createInsuranceCompareModel(nikoPatientDetail);
        nikoPatientDetail.hasHistory = !nikoPatientDetail.isNew &&
            (
                (nikoPatientDetail.primaryActive.insuranceId.current != nikoPatientDetail.primaryActive.insuranceId.initial || nikoPatientDetail.primaryActive.policyNo.current != nikoPatientDetail.primaryActive.policyNo.initial) ||
                (nikoPatientDetail.secondaryActive.insuranceId.current != nikoPatientDetail.secondaryActive.insuranceId.initial || nikoPatientDetail.secondaryActive.policyNo.current != nikoPatientDetail.secondaryActive.policyNo.initial) ||
                (nikoPatientDetail.primaryInactive.insuranceId.current && (nikoPatientDetail.primaryInactive.insuranceId.current != nikoPatientDetail.primaryInactive.insuranceId.initial || nikoPatientDetail.primaryInactive.policyNo.current != nikoPatientDetail.primaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.secondaryInactive.insuranceId.current && (nikoPatientDetail.secondaryInactive.insuranceId.current != nikoPatientDetail.secondaryInactive.insuranceId.initial || nikoPatientDetail.secondaryInactive.policyNo.current != nikoPatientDetail.secondaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.eligibilityStatusId != (nikoPatientDetail.initialNikoPatientDetail ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatusId : "")) ||
                nikoPatientDetail.patientTemplates.some(pt => !pt.isNew && pt.isPriorAuthNeeded != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded))
            );
    }

    @action.bound
    createInsuranceCompareModel(nikoPatientDetail) {
        nikoPatientDetail.primaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.primaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { olinitiald: null, current: null },
        };
        nikoPatientDetail.secondaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };

        const fill = (list, targetKey) => {
            if (!list) return;
            const map = targetKey === 'current' ?
                { type: 'type', status: 'status', id: 'id', insuranceId: 'insuranceId', insurance: 'insurance', policyNo: 'policyNo', startDate: 'startDate', endDate: 'endDate' }
                :
                { type: 'Type', status: 'Status', id: 'Id', insuranceId: 'InsuranceId', insurance: 'Insurance', policyNo: 'PolicyNo', startDate: 'StartDate', endDate: 'EndDate' };

            list.forEach(i => {
                const key = `${i[map.type].toLowerCase()}${i[map.status]}`;
                const target = nikoPatientDetail[key];
                if (!target) return;
                target.id[targetKey] = i[map.id];
                target.insuranceId[targetKey] = i[map.insuranceId];
                target.insurance[targetKey] = i[map.insurance];
                target.policyNo[targetKey] = i[map.policyNo];
                target.startDate[targetKey] = i[map.startDate];
                target.endDate[targetKey] = i[map.endDate];
            });
        };

        fill(nikoPatientDetail.nikoPatientDetailInsurances, 'current');
        if (nikoPatientDetail.initialNikoPatientDetail) {
            fill(nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances, 'initial');
            resolveInitialEligibilityStatus(nikoPatientDetail.initialNikoPatientDetail, this.eligibilityStatuses);
        }
    }

    @action.bound
    getPatientStatusButtonClassName(nikoPatientDetail, status = null) {
        nikoPatientDetail.hospitalClassName = "";
        nikoPatientDetail.dischargedClassName = "";
        nikoPatientDetail.expiredClassName = "";
        nikoPatientDetail.activeClassName = "";

        this.statuses.filter(i => !status || i.id === status).forEach(() => {
            nikoPatientDetail.activeClassName = "is-active";
        });
    }

    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderingTypeId}`);
    }

    @action.bound
    async getFacilityOrderStatus() {
        var statuses = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        runInAction(() => {
            this.facilityOrder.facilityOrderStatuses.replace(statuses.data.item);
        });
        this.refreshKey = uuid();
    }

    @action.bound
    filterByQuestion(selected) {
        this.selectedQuestions = selected.value;
        // this.filterChange = true;
        this.queryUtility.fetch();
    }

    @action.bound
    filterByCategory(selected) {
        this.itemCategories = selected.value;
        // this.filterChange = true
        this.queryUtility.fetch();
    }

    @action.bound
    handleActionDropDownItemsOnClick(e) {
        // if (e.item.text === "New Patient") {
        //     this.goToCreatePatient();
        // }
        if (e.item.text === "Find and Replace") {
            this.openNikoFacilityOrderUSIntakeFindAndReplaceModal();
        }
        // if (e.item.text === "Export FO CSV") {
        //     this.downloadFacilityOrderCsv();
        // }
        if (e.item.text === "Inventory Request Email") {
            this.openInventoryRequestWindowThroughActionsMenu();
        }
        else if (e.item.text === "Inventory Report") {
            this.downloadInventoryReport();
        }
        else {
            return e;
        }
    }

    @action.bound
    async openNikoPatientStatusModal(nikoPatientDetail, status) {
        this.nikoPatientStatusModal.open({
            status: status,
            nikoPatientDetail: nikoPatientDetail,
            pageName: this.pageName,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
        });
    }

    @action.bound
    changeItemsVisibility(patientTemplate) {
        patientTemplate.itemsVisible = !patientTemplate.itemsVisible;
    }

    @action.bound
    async openNikoPatientInsuranceQuestionModal(nikoPatientDetail) {
        this.nikoPatientInsuranceQuestionModal.open({
            nikoPatientDetail: nikoPatientDetail,
        });
    }

    @action.bound
    async openNikoPatientInsuranceInfoNoteModal(nikoPatientDetail) {
        this.nikoPatientInsuranceInfoNoteModal.open({
            nikoPatientDetail: nikoPatientDetail,
            setTempValue: (nikoPatientDetail) => { nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote }
        });
    }

    @action.bound
    async updateNikoPatientInsuranceInfoNote(nikoPatientDetail, value, save = false) {
        nikoPatientDetail.tempInsuranceInfoNote = value;
        if (save) {
            nikoPatientDetail.insuranceInfoNote = nikoPatientDetail.tempInsuranceInfoNote;
            await this.updateInsuranceInfo(nikoPatientDetail);
        }
    }

    @action.bound
    async updateInsuranceInfo(nikoPatientDetail) {
        nikoPatientDetail.disabled = true;
        try {
            await this.nikoPatientDetailService.updatePatientInsuranceInfo(nikoPatientDetail);
        }
        catch (err) {
            this.rootStore.notificationStore.error(getErrorMessage(err, 'Error occurred while trying to update patient Insurance Information.'));
            // await this.getNikoPatient();
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async openNikoHealthPatient(remoteId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/patients/${remoteId}/demographics`);
    }

    @action.bound
    goToNikoHealthSalesOrder(salesOrderId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/orders/order/${salesOrderId}/details`);
    }

    @action.bound
    goToNikoHealthInvoice(invoiceId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/billing/invoices/invoice/${invoiceId}/details`);
    }

    // ISG-2663: attach Niko invoice ids ({ remoteId, displayId }, oldest-first) to each template for the header.
    async attachNikoInvoiceIds(templates) {
        const ids = templates.map(t => t.id).filter(Boolean);
        if (!ids.length) return;
        const invoiceMap = (await this.patientTemplateService.getNikoInvoiceIds(ids)).data || {};
        const invoicesByTemplateId = {};
        Object.keys(invoiceMap).forEach(key => { invoicesByTemplateId[key.toLowerCase()] = invoiceMap[key]; });
        templates.forEach(t => {
            t.nikoInvoices = invoicesByTemplateId[(t.id || '').toLowerCase()] || [];
        });
    }

    @action.bound
    async openInventoryRequestWindow() {
        this.emailType = (await this.inventoryRequestService.checkIsNikoInventoryRequestEligible(this.facilityOrder.id)).data;
        window.open(`${this.rootStore.routerStore
            .findRoute('master.app.main.niko-facility-order.niko-inventory-request.new-flow-edit-email')
            .pattern.replace(":facilityOrderId", this.id)
            .replace(":inventoryRequestType", this.emailType)}?isNiko=true
        `);
    }

    @action.bound
    async openInventoryRequestWindowThroughActionsMenu() {
        this.emailType = (await this.inventoryRequestService.checkIsNikoInventoryRequestEligible(this.facilityOrder.id)).data;
        if (this.emailType == 'None') {
            this.rootStore.notificationStore.error("Inventory is not necessary because we only supply covered patients in this facility.");
        }
        else {
            window.open(`${this.rootStore.routerStore
                .findRoute('master.app.main.niko-facility-order.niko-inventory-request.new-flow-edit-email')
                .pattern.replace(":facilityOrderId", this.id)
                .replace(":inventoryRequestType", this.emailType)}?isNiko=true
            `);
        }
    }

    @action.bound
    downloadInventoryReport() {
        const facilityInventoryCountDetailService = new FacilityInventoryCountDetailService(this.rootStore.application.baasic.apiClient);
        const downloadUrl = `${facilityInventoryCountDetailService.getDownloadNikoInventoryReportUrl({ facilityOrderId: this.facilityOrder.id })}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(downloadUrl, "_blank");
    }

    @action.bound
    downloadPsoReport() {
        const filter = {
            facilityId: this.facilityStore.value && this.facilityStore.value.id,
            patientId: this.patientStore.value && this.patientStore.value.id,
            itemCategoryId: this.itemCategoryStore.value && this.itemCategoryStore.value.id,
            billingValidation: this.billingValidationStore.value && this.billingValidationStore.value.id,
            dosDateFrom: this.dosFilter.start ? toDateOnlyString(this.dosFilter.start) : undefined,
            dosDateTo: this.dosFilter.end ? toDateOnlyString(this.dosFilter.end) : undefined,
        };
        const response = this.nikoPatientDetailService.downloadPsoReport(filter);
        const downloadUrl = `${response}${response.endsWith('pso-report/') ? '?' : '&'}contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(downloadUrl, "_blank");
    }

    @action.bound
    async openNikoFacilityOrderUSIntakeFindAndReplaceModal() {
        let errorMessage = null;

        if (errorMessage) {
            this.rootStore.notificationStore.error(errorMessage);
            return;
        }
        this.nikoFacilityOrderUSIntakeFindAndReplaceModal.open({
            facilityOrderId: this.facilityOrder.id,
            fetchData: async () => { this.queryUtility.fetch(); }
        });
    }

    @action.bound
    async signalRAction(message) {
        if (message.table == "SE") {
            if (message.column == "EmailType" && message.id == this.facilityOrder.id) {
                if (message.text == "InventoryRequest") {
                    this.getFacilityOrderStatus();
                }
            }
        }
    }

    @action.bound
    showPreviousFlag(nikoPatientDetail) {
        return nikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail && (nikoPatientDetail.status != nikoPatientDetail.initialNikoPatientDetail.Status);
    }

    @action.bound
    async reviewDischargePatientTemplates() {
        //         this.disabledPatient = true;
        //         await this.saveTemplates(facilityOrderTemplateId, true);
        //         this.disabledPatient = false;
    }

    @action.bound
    async openReasonForChangeModal(nikoPatientDetail) {
        this.nikoFacilityOrderReasonForChangeModal.open({
            nikoPatientDetail: nikoPatientDetail,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
            sendOrder: this.facilityOrder && this.facilityOrder.sendOrder,
            // ISG-2625: only the PSO page restricts the second (Insurance Plans) tab once a Niko Sales Order exists.
            pageName: 'pso',
            showTemplateChangeWarning: (nikoPatientDetail, eligibilityStatusChange, nonEligibilityStatusChange, isParUpdated) => this.showTemplateChangeWarning(nikoPatientDetail, eligibilityStatusChange, nonEligibilityStatusChange, isParUpdated)
        });
    }

    @action.bound
    async openChangeClinicalStatusModal(patientTemplate, nikoPatientDetail) {
        const nikoPatient = nikoPatientDetail && nikoPatientDetail.nikoPatient;
        const patientName = nikoPatient
            ? `${nikoPatient.lastName || ''}${nikoPatient.lastName && nikoPatient.firstName ? ', ' : ''}${nikoPatient.firstName || ''}`
            : '';
        this.changeClinicalStatusModal.open({
            patientTemplate: patientTemplate,
            patientName: patientName,
            // ISG-2727: the multi-DOS credit pop-up View links filter the PSO page by patient as well as by PSO id.
            nikoPatientId: nikoPatient && nikoPatient.id,
            // ISG-2898: master-patient templates get the master-patient Clinical Status list.
            isMasterPatient: nikoPatientDetail.facility
                && nikoPatientDetail.facility.nikoMasterPatientId
                && nikoPatientDetail.facility.nikoMasterPatientId == nikoPatientDetail.nikoPatientId,
            getSalesOrders: () => this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId),
            // ISG-3039: refreshes every PSO the change touched (the source plus any multi-DOS credited ones).
            refreshPatientTemplates: (ids) => this.refreshPatientTemplatesByIds(ids)
        });
    }

    @action.bound
    async openNikoPatientEligibilityStatusNoteModal(nikoPatientDetail) {
        this.nikoPatientEligibilityStatusNoteModal.open({
            nikoPatientDetail: nikoPatientDetail,
        });
    }

    @action.bound
    showTemplateChangeWarning(
        nikoPatientDetail,
        eligibilityStatusChange,
        nonEligibilityStatusChange,
        isParUpdated,
        template
    ) {
        var message = undefined;
        if (isParUpdated) {
            message = "PAR flag was updated on a clinically reviewed template. Please ensure that the change you made is accounted for."
        }
        else if (nonEligibilityStatusChange && this.clinicalReviewStatus && this.clinicalReviewStatus.completedOn) {
            message = "Clinical review has been completed. Please ensure that the change you made is accounted for."
        }
        else if (eligibilityStatusChange && nikoPatientDetail && nikoPatientDetail.patientTemplates.find(x => x.isClinicalReviewed)) {
            message = "At least one of this patient's templates has already been reviewed by Clinical Review. Please ensure that the change you made is accounted for."
        }
        else if (template && template.isClinicalReviewed) {
            message = "This template has already been reviewed by Clinical Review. Please ensure that the change you made is accounted for."
        }

        if (message) {
            //this.templateChangeConfirmModal.open({ message: message }); TODO: resolve when specificacion is defined
        }
    }

    @action.bound
    async reviewNikoPatient(nikoPatientDetail) {
        nikoPatientDetail.disabled = true;
        nikoPatientDetail.isInsuranceReviewed = !nikoPatientDetail.isInsuranceReviewed;
        await this.nikoPatientDetailService.update(nikoPatientDetail);
        nikoPatientDetail.disabled = false;
    }

    @action.bound
    async openPatientTemplateInvoiceNoteModal(patientTemplate) {
        this.nikoTemplateInvoiceNoteModal.open({
            patientTemplate: patientTemplate,
            isEditDisabled: Boolean(patientTemplate.isEditDisabled),
            showTemplateChangeWarning: () => this.showTemplateChangeWarning(undefined, false, false, false, patientTemplate)
        });
    }

    @action.bound
    async openPatientTemplateMissingProductNoteModal(patientTemplate) {
        this.nikoTemplateMissingProductNoteModal.open({
            patientTemplate: patientTemplate,
            isEditDisabled: true,
            showTemplateChangeWarning: () => this.showTemplateChangeWarning(undefined, false, false, false, patientTemplate)
        });
    }

    @action.bound
    async openPatientTemplateNoteModal(patientTemplate) {
        this.patientTemplateNoteModal.open({
            patientTemplate: patientTemplate
        });
    }

    @action.bound
    async openPatientTemplateDiscontinuedModal(patientTemplate) {
        this.patientTemplateDiscontinuedModal.open({
            icon: "cross-circle",
            title: `${patientTemplate.isDiscontinued ? "Unmark" : "Mark"} As Discontinued`,
            description: patientTemplate.isDiscontinued ? "Are you sure you want to unmark this order as Discontinued?" : undefined,
            action: () => this.markDiscontinued(patientTemplate)
        });
    }

    @action.bound
    async markDiscontinued(patientTemplate) {
        // ISG-871: the backend runs drainage bag / leg bag validations when activating an Active patient's template and returns warnings.
        const response = await this.patientTemplateService.updateDiscontinuedStatus({ id: patientTemplate.id, isDiscontinued: !patientTemplate.isDiscontinued, pageName: this.pageName });
        await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);

        const validationMessages = response && response.data && response.data.messages;
        if (validationMessages && validationMessages.length) {
            validationMessages.forEach((message) => this.rootStore.notificationStore.warning(message));
        }
    }

    @action.bound
    async openPatientTemplateDoNotSupplyModal(patientTemplate) {
        this.patientTemplateDoNotSupplyModal.open({
            icon: "over-circle",
            title: `${patientTemplate.isUserDns ? "Unmark" : "Mark"} As Do Not Supply`,
            description: patientTemplate.isUserDns ? "Are you sure you want to unmark this order as Do Not Supply?" : undefined,
            action: () => this.markDoNotSupply(patientTemplate)
        });
    }

    @action.bound
    async markDoNotSupply(patientTemplate) {
        patientTemplate.isUserDns = !patientTemplate.isUserDns;
        await this.patientTemplateService.update(patientTemplate);
        this.showTemplateChangeWarning(undefined, false, false, false, patientTemplate);
        // await this.getTemplates()
    }

    @action.bound
    async openPatientTemplateDeleteModal(nikoPatientDetail, patientTemplate) {
        let params = { facilityOrderId: patientTemplate.facilityOrderId, nikoPatientDetailId: patientTemplate.nikoPatientDetailId, pageSize: 3, fields: 'Id,TemplateId' };
        var response = (await this.patientTemplateService.find(params)).data.item;
        this.patientTemplateDeleteModal.open({
            item: patientTemplate,
            deleteTemplate: () => this.deleteTemplate(patientTemplate),
            numberOfTemplates: response.every(x => x.id == patientTemplate.id || x.templateId == patientTemplate.id) ? 1 : 2,
            isNewPatient: nikoPatientDetail.isNew
        });
    }

    @action.bound
    async deleteTemplate(patientTemplate) {
        patientTemplate.disabled = true;
        try {
            await this.patientTemplateService.deleteNewTemplate(patientTemplate.id);
            await this.fetchPatientTemplateByPatient(patientTemplate.nikoPatientDetailId);
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occurred while trying to delete a template.");
        }
        finally {
            patientTemplate.disabled = false;
        }
    }

    @action.bound
    async updatePatientTemplateNote(patientTemplate) {
        patientTemplate.note = patientTemplate.tempNote;
        await this.patientTemplateService.update(patientTemplate);
    }

    @action.bound
    async unreviewTemplate() {
        //         this.disabledPatient = true;
        //         await this.reviewActiveTemplates("in-progress", facilityOrderTemplateId, false);
        //         this.disabledPatient = false;
    }

    @action.bound
    isDisabledTemplateReview(nikoPatientDetail) {
        return (nikoPatientDetail.patientTemplates.some(x => !x.isNew) && nikoPatientDetail.insuranceStatusId === this.inProgressStatusId) ||
            ((nikoPatientDetail.isNew || nikoPatientDetail.insuranceStatusId === this.changeStatusId) && !nikoPatientDetail.isInsuranceReviewed);
    }

    @action.bound
    async addEmptyItem(patientTemplate) {
        if (patientTemplate.patientTemplateItems.some(i => !i.productId)) {
            return;
        }

        await this.setProductStore(patientTemplate);
        patientTemplate.patientTemplateItems.push({
            id: uuid(),
            quantity: 0,
            salesOrderQuantity: null,
            note: undefined,
            itemCals: undefined,
            itemMls: undefined,
            mlph: undefined,
            frequency: undefined,
            procedureCode: null,
            patientTemplatId: patientTemplate.id,
            productId: null,
            product: null,
            actionId: this.insertImportActionId,
            toggle: !this.templateChangeConfirmModal.isOpen
        });
    }

    @action.bound
    async setProductStore(patientTemplate) {
        patientTemplate.productStore = new BaasicDropdownStore({
            filterable: true,
            textField: "itemId",
        }, {
            fetchFunc: async (term) => {
                if ((term && term.trim()) === '')
                    term = undefined;

                let params = {
                    searchQuery: term,
                    rpp: 50,
                    subCategoryId: patientTemplate.subCategoryId,
                    isActive: true,
                    embed: "Product,Product.ItemType,Product.ItemCategory,Product.SubCategory,Product.UnitOfMeasure,Product.ProcedureCode_"
                }

                if (this.ostomySubCategoryId == patientTemplate.subCategoryId) {
                    params = { ...params, sort: "isPreferred|desc" };
                }

                const response = await this.itemSubcategoryService.find(params);
                let filteredProducts = response.data.item.filter(x => !x.product.itemId.toLowerCase().includes("-cs"));
                return _.map(filteredProducts, item => {
                    return {
                        ...item.product
                    };
                });
            }
        });
    }

    @action.bound
    orderHistory() {
        window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.history").pattern.replace(":id", this.facilityOrder.id));
    }

    @action.bound
    getChangedFields(nikoPatientDetail) {
        var fields = [];
        var npd = nikoPatientDetail;
        if (!npd.isNew) {
            if (npd.primaryActive.insuranceId.current != npd.primaryActive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryActiveInsuranceId", value: npd.primaryActive.insuranceId.current || null, initialValue: npd.primaryActive.insuranceId.initial || null });
            if (npd.primaryActive.insuranceId.current == npd.primaryActive.insuranceId.initial && npd.primaryActive.policyNo.current != npd.primaryActive.policyNo.initial)
                fields.push({ fieldName: "PrimaryActivePolicyNo", value: npd.primaryActive.policyNo.current || null, initialValue: npd.primaryActive.policyNo.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current != npd.primaryInactive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryInactiveInsuranceId", value: npd.primaryInactive.insuranceId.current || null, initialValue: npd.primaryInactive.insuranceId.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current == npd.primaryInactive.insuranceId.initial && npd.primaryInactive.policyNo.current != npd.primaryInactive.policyNo.initial)
                fields.push({ fieldName: "PrimaryInactivePolicyNo", value: npd.primaryInactive.policyNo.current || null, initialValue: npd.primaryInactive.policyNo.initial || null });
            if (npd.secondaryActive.insuranceId.current != npd.secondaryActive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryActiveInsuranceId", value: npd.secondaryActive.insuranceId.current || null, initialValue: npd.secondaryActive.insuranceId.initial || null });
            if (npd.secondaryActive.insuranceId.current == npd.secondaryActive.insuranceId.initial && npd.secondaryActive.policyNo.current != npd.secondaryActive.policyNo.initial)
                fields.push({ fieldName: "SecondaryActivePolicyNo", value: npd.secondaryActive.policyNo.current || null, initialValue: npd.secondaryActive.policyNo.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current != npd.secondaryInactive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryInactiveInsuranceId", value: npd.secondaryInactive.insuranceId.current || null, initialValue: npd.secondaryInactive.insuranceId.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current == npd.secondaryInactive.insuranceId.initial && npd.secondaryInactive.policyNo.current != npd.secondaryInactive.policyNo.initial)
                fields.push({ fieldName: "SecondaryInactivePolicyNo", value: npd.secondaryInactive.policyNo.current || null, initialValue: npd.secondaryInactive.policyNo.initial || null });
            if (npd.eligibilityStatusId != (npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId : null))
                fields.push({ fieldName: "EligibilityStatusId", value: npd.eligibilityStatusId || null, initialValue: npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId || null : null });
            npd.patientTemplates.forEach(function (pt) {
                if (!pt.isNew && (pt.isPriorAuthNeeded ? true : false) != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? true : false))
                    fields.push({ fieldName: "PAR_" + pt.id, value: pt.isPriorAuthNeeded ? "TRUE" : "FALSE", initialValue: pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? "TRUE" : "FALSE" });
            });
        }
        if (npd.note)
            fields.push({ fieldName: "Note", value: npd.note, initialValue: null });
        return fields;
    }

    @action.bound
    setShowInsuranceChangeHistory(value) {
        this.showInsuranceChangeHistory = value;
    }

    @action.bound
    async fetchInsuranceChangeHistory(nikoPatientDetail) {
        var fields = this.getChangedFields(nikoPatientDetail);
        if (fields.length === 0) return;
        try {
            const response = await this.historyService.getInsuranceChangeHistory(nikoPatientDetail.id, fields);
            const data = response.data || response;
            var historyMap = {};
            if (Array.isArray(data)) {
                data.forEach(function (entry) {
                    historyMap[entry.fieldName] = entry;
                });
            }
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: historyMap });
            });
        } catch (err) {
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: {} });
            });
        }
    }

    @action.bound
    async getFieldHistory(item, fieldName, value) {
        if (item.history) {
            let history;
            if (fieldName === 'ImportAction') {
                history = (await this.historyService.getItemActionHistory(item.id)).data;
            } else {
                history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
            }
            item.history.userName = history.userName;
            item.history.eventDate = history.eventDate;
            item.history.fieldName = fieldName;
            if (history.action) item.history.action = history.action;
        }
    }

    @action.bound
    setDosFilter(event) {
        const value = event.target ? event.target.value : event.value;
        this.tempDosFilter = { start: value.start, end: value.end };
    }

    // Individual "x" clear of the From/To field keeps recomputing the other filters
    // immediately (no blur/Apply required, no no-results check — clearing never yields the toast).
    @action.bound
    clearDosFilter(event) {
        const value = event.target ? event.target.value : event.value;
        this.tempDosFilter = { start: value.start, end: value.end };
        this.refreshDependentFilterOptions('dos');
    }

    // Triggered when the user leaves the From/To input (blur) or clicks Apply in the date
    // picker. Recomputes the other four filters and, for a range that returns no PSOs, shows the
    // no-results toast and clears the Dates filter. An open-ended range with only a "From" or only a
    // "To" is valid, so the no-results check runs whenever either end is set; only a genuinely invalid
    // inverted full range (start after end) is ignored.
    @action.bound
    async commitDosFilter() {
        const { start, end } = this.tempDosFilter;
        if (start && end && start > end) return;

        if (start || end) {
            const hasResults = await this.checkDosRangeHasResults();
            if (!hasResults) {
                this.rootStore.notificationStore.error("There are no PSOs to display for the selected date range.");
                this.dosFilter = { start: null, end: null };
                this.tempDosFilter = { start: null, end: null };
            }
        }
        this.refreshDependentFilterOptions('dos');
    }

    async checkDosRangeHasResults() {
        const params = {
            facilityId: (this.facilityStore.value && this.facilityStore.value.id) || undefined,
            nikoPatientId: (this.patientStore.value && this.patientStore.value.id) || undefined,
            itemCategoryIds: this.itemCategoryStore.value && this.itemCategoryStore.value.id ? [this.itemCategoryStore.value.id] : undefined,
            billingValidation: (this.billingValidationStore.value && this.billingValidationStore.value.id) || undefined,
            hasMissingDocs: this.queryUtility.filter.hasMissingDocs,
            clinicalStatusId: this.queryUtility.filter.clinicalStatusId || undefined,
            isSystemDns: this.queryUtility.filter.isSystemDns,
            isUserDns: this.queryUtility.filter.isUserDns,
            dosDateFrom: this.tempDosFilter.start ? toDateOnlyString(this.tempDosFilter.start) : undefined,
            dosDateTo: this.tempDosFilter.end ? toDateOnlyString(this.tempDosFilter.end) : undefined,
            isSalesOrder: true,
            pageSize: 1,
        };
        const responseData = (await this.patientTemplateService.find(params)).data.item;
        return !!(responseData && responseData.length > 0);
    }

    @action.bound
    async completeTemplateValidation(patientTemplateId, validationId) {
        try {
            await this.facilityOrderTemplateValidationService.resolve({ id: validationId });
            const nikoPatientDetail = this.nikoPatientDetails.find(npd =>
                npd.patientTemplates.some(pt => pt.id === patientTemplateId)
            );
            if (nikoPatientDetail) {
                const patientTemplate = nikoPatientDetail.patientTemplates.find(pt => pt.id === patientTemplateId);
                if (patientTemplate) {
                    const validation = patientTemplate.facilityOrderTemplateValidations.find(v => v.id === validationId);
                    if (validation) {
                        runInAction(() => { validation.isResolved = true; });
                    }
                }
            }
        } catch (err) {
            this.rootStore.notificationStore.error("Error resolving validation.");
        }
    }

    @action.bound
    async rentalsCapped(nikoPatientId, facilityOrderId, nikoPatientDetailId) {
        try {
            await this.nikoPatientService.rentalsCapped({ id: nikoPatientId, rentalsCapped: true, facilityOrderId: facilityOrderId });
            this.rootStore.notificationStore.success("Rentals capped successfully.");
            const nikoPatientDetail = this.nikoPatientDetails.find(npd => npd.id === nikoPatientDetailId);
            if (nikoPatientDetail) {
                const patientTemplates = nikoPatientDetail.patientTemplates.filter(pt => pt.facilityOrderId === facilityOrderId);
                patientTemplates.forEach(template => {
                    runInAction(() => {
                        template.facilityOrderTemplateValidations.forEach(v => {
                            v.isResolved = true;
                        });
                    });
                });
            }
        } catch (err) {
            this.rootStore.notificationStore.error("Error marking rentals as capped.");
        }
    }
}

export default NikoPatientSalesOrderViewStore;

